package ai.wagemix.logic.mixes

import ai.wagemix.logic.lapsedFlow
import ai.wagemix.data.statsSignature
import ai.wagemix.net.models.name
import ai.wagemix.net.models.WMStatsCollection
import ai.wagemix.data.touchesUser
import ai.wagemix.data.WMMixSyncEvent
import ai.wagemix.logic.isForUser
import ai.wagemix.logic.isGlobal
import ai.wagemix.logic.mixbuilder.toDraft
import ai.wagemix.logic.mixes.WMSavedMixesPresenter.Action
import ai.wagemix.logic.mixes.WMSavedMixesPresenter.Event
import ai.wagemix.logic.mixes.WMSavedMixesPresenter.Prompt
import ai.wagemix.logic.retrieveStoredMix
import ai.wagemix.logic.sendWMMixOpened
import ai.wagemix.logic.session
import ai.wagemix.logic.storedMixesFlow
import ai.wagemix.logic.wages.WM_DEFAULT_PROJECTED_YEARS
import ai.wagemix.logic.wages.toWageSummary
import ai.wagemix.logic.WMNavigationRequest
import ai.wagemix.logic.WMMixOpenedFrom
import ai.wagemix.logic.WMRoutes
import ai.wagemix.net.client.WMOccupationStatsCollectionRequestBuilder
import ai.wagemix.net.models.fromJobDescription
import ai.wagemix.net.models.lastChangedAt
import ai.wagemix.net.models.WMStoredMix
import com.fsryan.foundation.logic.FSPresenter
import com.fsryan.foundation.logic.FSPrompt
import com.fsryan.foundation.logic.presenterInstanceId
import com.fsryan.foundation.logic.prompting.defaultHandlePromptResponse
import com.fsryan.foundation.logic.prompting.FSBasePromptingPresenter
import com.fsryan.foundation.logic.prompting.FSPromptingPresenter
import com.fsryan.foundation.models.FSPropertyContainer
import com.fsryan.foundation.util.log.FSLog
import kotlin.jvm.JvmInline
import kotlin.math.roundToInt
import kotlin.time.Clock
import kotlin.time.Instant
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.filter
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.onEach
import kotlinx.coroutines.flow.onStart
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime

enum class WMSavedMixesFilter { All, FromJobDescription }

/** One card of the saved-mixes grid. */
data class WMSavedMixCard(
    val mixId: String,
    val title: String,
    val occupationCount: Int,
    val fromJobDescription: Boolean,
    val lastChangedAt: Instant?,
    val areaId: Int?,
    val industryId: Int?,
    /** The current-year annual median, when the mix's stats have arrived. */
    val median: Double?,
    /** Annual medians by year for the sparkline; empty until the stats arrive. */
    val sparkline: List<Double>,
    /**
     * The mix's heaviest enabled occupations, at most [WMSavedMixCard.TOP_OCCUPATIONS], heaviest first, so mixes can be
     * told apart by what is in them (design deviation C1). An occupation whose name is not known yet is left out.
     */
    val topOccupations: List<WMSavedMixCardOccupation> = emptyList()
) {
    companion object {
        const val TOP_OCCUPATIONS = 3
    }
}

/** One occupation on a saved-mix card: its [name] and its share of the mix in percent. */
@ConsistentCopyVisibility
data class WMSavedMixCardOccupation internal constructor(val occupationId: Int, val name: String, val percentage: Double)

/**
 * Saved mixes: the list with its filter and search, per-card wage summary, rename, duplicate,
 * delete, and opening a mix in the builder or the share dialog.
 */
interface WMSavedMixesPresenter<P: Prompt, out E: Event<P>, in A: Action>: FSPromptingPresenter<P, E, A> {

    interface Action: FSPromptingPresenter.Action {
        sealed interface Base: Action

        @JvmInline
        value class ComposedSuperAction internal constructor(val wrapped: FSPromptingPresenter.Action.Base): Base

        @ConsistentCopyVisibility
        data class SetListParameters internal constructor(val filter: WMSavedMixesFilter, val query: String, override val extendedProperties: Map<String, Any> = emptyMap()): Base, FSPropertyContainer<SetListParameters> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        @ConsistentCopyVisibility
        data class Open internal constructor(val mixId: String, override val extendedProperties: Map<String, Any> = emptyMap()): Base, FSPropertyContainer<Open> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        @ConsistentCopyVisibility
        data class Share internal constructor(val mixId: String, override val extendedProperties: Map<String, Any> = emptyMap()): Base, FSPropertyContainer<Share> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        @ConsistentCopyVisibility
        data class RequestRename internal constructor(val mixId: String, override val extendedProperties: Map<String, Any> = emptyMap()): Base, FSPropertyContainer<RequestRename> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        @ConsistentCopyVisibility
        data class ConfirmRename internal constructor(val mixId: String, val title: String, override val extendedProperties: Map<String, Any> = emptyMap()): Base, FSPropertyContainer<ConfirmRename> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        @ConsistentCopyVisibility
        data class Duplicate internal constructor(val mixId: String, override val extendedProperties: Map<String, Any> = emptyMap()): Base, FSPropertyContainer<Duplicate> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        @ConsistentCopyVisibility
        data class RequestDelete internal constructor(val mixId: String, override val extendedProperties: Map<String, Any> = emptyMap()): Base, FSPropertyContainer<RequestDelete> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        interface Factory<P: Prompt, out A: Action>: FSPromptingPresenter.Action.Factory<P, A> {
            fun createSetListParameters(filter: WMSavedMixesFilter, query: String, extendedProperties: Map<String, Any> = emptyMap()): A
            fun createOpen(mixId: String, extendedProperties: Map<String, Any> = emptyMap()): A
            fun createShare(mixId: String, extendedProperties: Map<String, Any> = emptyMap()): A
            fun createRequestRename(mixId: String, extendedProperties: Map<String, Any> = emptyMap()): A
            fun createConfirmRename(mixId: String, title: String, extendedProperties: Map<String, Any> = emptyMap()): A
            fun createDuplicate(mixId: String, extendedProperties: Map<String, Any> = emptyMap()): A
            fun createRequestDelete(mixId: String, extendedProperties: Map<String, Any> = emptyMap()): A
        }

        companion object {
            fun <P: Prompt> baseFactory(): Factory<P, Base> = object: Factory<P, Base> {
                private val base = FSPromptingPresenter.Action.baseFactory<P>()
                override fun createUserRespondedToPrompt(response: FSPrompt.Response<P>, extendedProperties: Map<String, Any>): Base = ComposedSuperAction(base.createUserRespondedToPrompt(response, extendedProperties))
                override fun createSetListParameters(filter: WMSavedMixesFilter, query: String, extendedProperties: Map<String, Any>): Base = SetListParameters(filter, query, extendedProperties)
                override fun createOpen(mixId: String, extendedProperties: Map<String, Any>): Base = Open(mixId, extendedProperties)
                override fun createShare(mixId: String, extendedProperties: Map<String, Any>): Base = Share(mixId, extendedProperties)
                override fun createRequestRename(mixId: String, extendedProperties: Map<String, Any>): Base = RequestRename(mixId, extendedProperties)
                override fun createConfirmRename(mixId: String, title: String, extendedProperties: Map<String, Any>): Base = ConfirmRename(mixId, title, extendedProperties)
                override fun createDuplicate(mixId: String, extendedProperties: Map<String, Any>): Base = Duplicate(mixId, extendedProperties)
                override fun createRequestDelete(mixId: String, extendedProperties: Map<String, Any>): Base = RequestDelete(mixId, extendedProperties)
            }
        }
    }

    interface Event<out P: Prompt>: FSPromptingPresenter.Event<P> {
        /** The cards that match the current filter and query. */
        val cardsUpdate: List<WMSavedMixCard>?
        /** Whether the user has any mixes at all (drives the empty state vs "no matches"). */
        val hasMixesUpdate: Boolean?
        val filterUpdate: WMSavedMixesFilter?
        val queryUpdate: String?
        /** The mix the share dialog should open for. */
        val shareRequestUpdate: String?
        val navigationUpdate: WMNavigationRequest?
        /**
         * Whether the account has lapsed: the screen shows the renewal reminder instead of its content, and the content
         * comes back on renewal (planning/paid-feature-enforcement-plan.md W7). `null` means no change.
         */
        val lapsedUpdate: Boolean?

        @ConsistentCopyVisibility
        data class Base<out P: Prompt> internal constructor(
            private val base: FSPromptingPresenter.Event.Base<P>,
            override val cardsUpdate: List<WMSavedMixCard>? = null,
            override val hasMixesUpdate: Boolean? = null,
            override val filterUpdate: WMSavedMixesFilter? = null,
            override val queryUpdate: String? = null,
            override val shareRequestUpdate: String? = null,
            override val navigationUpdate: WMNavigationRequest? = null,
            override val lapsedUpdate: Boolean? = null
        ): Event<P>, FSPropertyContainer<Base<P>>, FSPromptingPresenter.Event<P> by base {
            override val extendedProperties: Map<String, Any> get() = base.extendedProperties
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(base = base.replaceExtendedProperties(properties))
        }

        interface Factory<P: Prompt, out E: Event<P>>: FSPromptingPresenter.Event.Factory<P, E> {
            fun createListUpdate(cards: List<WMSavedMixCard>, hasMixes: Boolean, filter: WMSavedMixesFilter, query: String): E
            fun createShareRequest(mixId: String): E
            fun createNavigation(request: WMNavigationRequest): E
            fun createLapsedUpdate(lapsed: Boolean): E
        }

        companion object {
            fun <P: Prompt> baseFactory(): Factory<P, Base<P>> = object: Factory<P, Base<P>> {
                private val base = FSPromptingPresenter.Event.baseFactory<P>()
                override fun createPromptChange(prompt: P, loading: Boolean?, extendedProperties: Map<String, Any>) = Base(base.createPromptChange(prompt, loading, extendedProperties))
                override fun createLoadingChange(loading: Boolean, extendedProperties: Map<String, Any>) = Base(base.createLoadingChange(loading, extendedProperties))
                override fun none() = Base(base.none())
                override fun createListUpdate(cards: List<WMSavedMixCard>, hasMixes: Boolean, filter: WMSavedMixesFilter, query: String) =
                    Base(base.none(), cardsUpdate = cards, hasMixesUpdate = hasMixes, filterUpdate = filter, queryUpdate = query)
                override fun createShareRequest(mixId: String) = Base(base.none(), shareRequestUpdate = mixId)
                override fun createNavigation(request: WMNavigationRequest) = Base(base.none(), navigationUpdate = request)
                override fun createLapsedUpdate(lapsed: Boolean) = Base(base.none(), lapsedUpdate = lapsed)
            }
        }
    }

    interface Prompt: FSPromptingPresenter.Prompt {
        sealed interface Base: Prompt

        @JvmInline
        value class ComposedSuperPrompt internal constructor(val wrapped: FSPromptingPresenter.Prompt.Base): Base

        /** The dialog collects the new title and answers through `userConfirmedRename`. */
        @ConsistentCopyVisibility
        data class RenameMix internal constructor(val mixId: String, val currentTitle: String): Base, FSPrompt.Displayed {
            override val forceInteraction = true
            override val messageOverride: String? = null
            override val titleOverride: String? = null
            enum class Selection { Cancel }
            fun selectionOf(selected: Int): Selection? = Selection.entries.getOrNull(selected)
        }

        @ConsistentCopyVisibility
        data class DeleteMix internal constructor(val mixId: String, val title: String): Base, FSPrompt.Displayed {
            override val forceInteraction = true
            override val messageOverride: String? = null
            override val titleOverride: String? = null
            enum class Selection { Cancel, Delete }
            fun selectionOf(selected: Int): Selection? = Selection.entries.getOrNull(selected)
        }

        interface Factory<out P: Prompt>: FSPromptingPresenter.Prompt.Factory<P> {
            fun renameMix(mixId: String, currentTitle: String): P
            fun deleteMix(mixId: String, title: String): P
        }

        companion object {
            fun baseFactory(): Factory<Base> = object: Factory<Base> {
                override fun createBase(wrapped: FSPrompt.Base): Base = ComposedSuperPrompt(FSPromptingPresenter.Prompt.baseFactory().createBase(wrapped))
                override fun renameMix(mixId: String, currentTitle: String): Base = RenameMix(mixId, currentTitle)
                override fun deleteMix(mixId: String, title: String): Base = DeleteMix(mixId, title)
            }
        }
    }

    fun userChangedListParameters(filter: WMSavedMixesFilter, query: String, extendedProperties: Map<String, Any> = emptyMap())
    fun userRequestedOpen(mixId: String, extendedProperties: Map<String, Any> = emptyMap())
    fun userRequestedShare(mixId: String, extendedProperties: Map<String, Any> = emptyMap())
    fun userRequestedRename(mixId: String, extendedProperties: Map<String, Any> = emptyMap())
    fun userConfirmedRename(mixId: String, title: String, extendedProperties: Map<String, Any> = emptyMap())
    fun userRequestedDuplicate(mixId: String, extendedProperties: Map<String, Any> = emptyMap())
    fun userRequestedDelete(mixId: String, extendedProperties: Map<String, Any> = emptyMap())

    companion object {
        const val SCREEN = "savedMixes"
        const val PARAM_FILTER = "filter"
        const val PARAM_QUERY = "query"
    }
}

/** The occupation titles the stats carry, by occupation id; empty until they arrive. */
private fun WMStatsCollection?.occupationTitles(): Map<Int, String> {
    return this?.orderedStats()?.associate { (id, stats) -> id to stats.title } ?: emptyMap()
}

/** The heaviest enabled occupations of the mix whose names are in [names] (the card's C1 list). */
internal fun WMStoredMix.topOccupations(names: Map<Int, String>): List<WMSavedMixCardOccupation> {
    return occupations
        .filter { it.isEnabled && it.percentage > 0 }
        .sortedByDescending { it.percentage }
        .mapNotNull { occupation -> names[occupation.id]?.let { WMSavedMixCardOccupation(occupation.id, it, occupation.percentage) } }
        .take(WMSavedMixCard.TOP_OCCUPATIONS)
}

/** The occupations a stored mix's stats request carries (weights are its percentages, tenths rounded to at least 1). */
internal fun WMStoredMix.weightedOccupations(): List<Pair<Int, Int>> {
    return occupations.filter { it.isEnabled && it.percentage > 0 }.map { it.id to (it.percentage * 10).roundToInt().coerceAtLeast(1) }
}

/** Shapes the API's stats request for this stored mix; the caller passes it to `syncStats`. */
internal fun WMStoredMix.storedStatsRequest(): WMOccupationStatsCollectionRequestBuilder.() -> Unit {
    val weighted = weightedOccupations()
    val area = filters.areaId
    val industry = filters.industryId
    return {
        area?.let { areaId(it) }
        industry?.let { industryId(it) }
        weighted.forEach { (id, weight) -> addOccupationId(id, weight) }
    }
}

/** The key the mix's stats live under in the global context, or `null` when it has nothing to request. */
internal fun WMStoredMix.storedStatsSignature(): String? {
    return if (weightedOccupations().isEmpty()) null else statsSignature(storedStatsRequest())
}

abstract class WMBaseSavedMixesPresenter<
    P: Prompt,
    E: Event<P>,
    A: Action,
    EF: Event.Factory<P, E>,
    AF: Action.Factory<P, A>,
    PF: Prompt.Factory<P>,
    G: WMSavedMixesDataGateway
>(
    protected val dataGateway: G,
    eventFactory: EF,
    actionFactory: AF,
    promptFactory: PF,
    cancelViewScopeOnUnready: Boolean,
    instanceId: String,
    protected val currentYear: () -> Int = { Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).year },
    handlePromptResponse: suspend (FSPrompt.Response<P>, suspend E.() -> Unit) -> Unit = { response, emit ->
        context(eventFactory, promptFactory, dataGateway) {
            // The syncs a response starts carry this presenter's id, so their success and failure come back here.
            val presenterContext = mapOf<String, Any>(FSPresenter.SYNC_EVENT_KEY_INSTANCE_ID to instanceId)
            defaultSavedMixesHandlePromptResponse(response, presenterContext, emit)
        }
    }
): FSBasePromptingPresenter<P, E, A, EF, AF, PF>(cancelViewScopeOnUnready, instanceId, eventFactory, actionFactory, promptFactory, handlePromptResponse),
    WMSavedMixesPresenter<P, E, A> {

    override fun userChangedListParameters(filter: WMSavedMixesFilter, query: String, extendedProperties: Map<String, Any>) = onUserAction(actionFactory.createSetListParameters(filter, query, extendedProperties))
    override fun userRequestedOpen(mixId: String, extendedProperties: Map<String, Any>) = onUserAction(actionFactory.createOpen(mixId, extendedProperties))
    override fun userRequestedShare(mixId: String, extendedProperties: Map<String, Any>) = onUserAction(actionFactory.createShare(mixId, extendedProperties))
    override fun userRequestedRename(mixId: String, extendedProperties: Map<String, Any>) = onUserAction(actionFactory.createRequestRename(mixId, extendedProperties))
    override fun userConfirmedRename(mixId: String, title: String, extendedProperties: Map<String, Any>) = onUserAction(actionFactory.createConfirmRename(mixId, title, extendedProperties))
    override fun userRequestedDuplicate(mixId: String, extendedProperties: Map<String, Any>) = onUserAction(actionFactory.createDuplicate(mixId, extendedProperties))
    override fun userRequestedDelete(mixId: String, extendedProperties: Map<String, Any>) = onUserAction(actionFactory.createRequestDelete(mixId, extendedProperties))

    @OptIn(ExperimentalCoroutinesApi::class)
    override fun registerSubscriptions() {
        dataGateway.lapsedFlow()
            .onEach { lapsed -> eventFactory.createLapsedUpdate(lapsed).emit() }
            .launchInViewScope()

        // The list is the mirror of the user's stored mixes; every stats arrival or list-parameter change re-derives the cards.
        dataGateway.cacheUpdateFlow
            .filter { it.touchesUser() }
            .map { dataGateway.session()?.uid }
            .onStart { emit(dataGateway.session()?.uid) }
            .distinctUntilChanged()
            .flatMapLatest { uid -> dataGateway.storedMixesFlow(uid).map { mixes -> uid to mixes } }
            .flatMapLatest { (uid, mixes) ->
                dataGateway.contextUpdateFlow
                    .filter { update ->
                        (update.isForUser(uid) && update.screenParamsChanged(WMSavedMixesPresenter.SCREEN)) ||
                            (update.isGlobal && mixes.any { mix -> mix.storedStatsSignature()?.let { update.statsChanged(it) } == true })
                    }
                    .map { uid to mixes }
                    .onStart { emit(uid to mixes) }
            }
            .onEach { (uid, mixes) -> emitList(uid, mixes) }
            .launchInViewScope()

        dataGateway.mixSyncEventFlow
            .filter { it.presenterInstanceId() == instanceId }
            .onEach { event ->
                when (event) {
                    is WMMixSyncEvent.RenameMix.Failure, is WMMixSyncEvent.DuplicateMix.Failure, is WMMixSyncEvent.DeleteMix.Failure -> {
                        eventFactory.createPromptChange(promptFactory.networkError(), loading = false).emit()
                    }
                    is WMMixSyncEvent.DeleteMix.Success -> {
                        FSLog.sendWMMixDeleted(mixId = event.mixId)
                        eventFactory.createLoadingChange(false).emit()
                    }
                    is WMMixSyncEvent.RenameMix.Success, is WMMixSyncEvent.DuplicateMix.Success -> {
                        eventFactory.createLoadingChange(false).emit()
                    }
                    else -> Unit
                }
            }
            .launchInViewScope()
    }

    override suspend fun sendInitialEvents(isFirstOnReadyCall: Boolean) = Unit   // the list flow emits on start

    override fun extractBasePromptingPresenterAction(action: A): FSPromptingPresenter.Action.Base? = (action as? Action.ComposedSuperAction)?.wrapped

    @Suppress("UNCHECKED_CAST")
    override fun FSPrompt.Response<*>.extract(): FSPrompt.Response<P> = this as FSPrompt.Response<P>

    override fun handleNonBasePromptingPresenterAction(action: A) {
        when (action) {
            is Action.SetListParameters -> launch {
                // The context update re-derives the list through the subscription above.
                dataGateway.upsertScreenParams(
                    dataGateway.session()?.uid,
                    WMSavedMixesPresenter.SCREEN,
                    mapOf(WMSavedMixesPresenter.PARAM_FILTER to action.filter.name, WMSavedMixesPresenter.PARAM_QUERY to action.query)
                )
            }
            is Action.Open -> launch {
                val uid = dataGateway.session()?.uid ?: return@launch
                val mix = dataGateway.retrieveStoredMix(uid, action.mixId) ?: return@launch
                dataGateway.upsertMixDraft(mix.toDraft(ownerUid = uid))
                dataGateway.upsertSelectedMixId(uid, mix.id)
                FSLog.sendWMMixOpened(mixId = mix.id, from = WMMixOpenedFrom.SavedMixes)
                eventFactory.createNavigation(WMNavigationRequest.Route(WMRoutes.mix(mix.id))).emit()
            }
            is Action.Share -> launch { eventFactory.createShareRequest(action.mixId).emit() }
            is Action.RequestRename -> launch {
                val title = currentMixes().firstOrNull { it.id == action.mixId }?.title ?: return@launch
                eventFactory.createPromptChange(promptFactory.renameMix(action.mixId, title)).emit()
            }
            is Action.ConfirmRename -> launch {
                val uid = dataGateway.session()?.uid ?: return@launch
                val title = action.title.trim().ifEmpty { return@launch }
                eventFactory.createPromptChange(promptFactory.none(), loading = true).emit()
                dataGateway.renameMix(uid, action.mixId, title, createPresenterContext(action))
            }
            is Action.Duplicate -> launch {
                val uid = dataGateway.session()?.uid ?: return@launch
                val title = currentMixes().firstOrNull { it.id == action.mixId }?.title ?: return@launch
                eventFactory.createLoadingChange(true).emit()
                dataGateway.duplicateMix(uid, action.mixId, "$title (copy)", createPresenterContext(action))
            }
            is Action.RequestDelete -> launch {
                val mix = currentMixes().firstOrNull { it.id == action.mixId } ?: return@launch
                // An AI mix stands for one of the plan's job-description analyses, so it can't be deleted.
                if (mix.fromJobDescription) {
                    return@launch
                }
                eventFactory.createPromptChange(promptFactory.deleteMix(action.mixId, mix.title)).emit()
            }
            else -> logUnknownActionType(action)
        }
    }

    /** A one-shot read of the mirrored list. */
    private suspend fun currentMixes(): List<WMStoredMix> {
        return dataGateway.retrieveUserContext(dataGateway.session()?.uid).storedMixes() ?: emptyList()
    }

    private suspend fun emitList(uid: String?, mixes: List<WMStoredMix>) {
        val params = dataGateway.retrieveUserContext(uid).screenParams(WMSavedMixesPresenter.SCREEN)
        val filter = params[WMSavedMixesPresenter.PARAM_FILTER]?.let { name -> WMSavedMixesFilter.entries.firstOrNull { it.name == name } } ?: WMSavedMixesFilter.All
        val query = params[WMSavedMixesPresenter.PARAM_QUERY] ?: ""
        val global = dataGateway.retrieveGlobalContext()
        val year = currentYear()
        val taxonomy = dataGateway.retrieveTaxonomy()?.data
        val taxonomyNames = taxonomy?.let { t -> (t.items + t.root).associate { it.id to it.name } } ?: emptyMap()
        val cards = mixes
            .filter { filter == WMSavedMixesFilter.All || it.fromJobDescription }
            .filter { query.isBlank() || it.title.contains(query.trim(), ignoreCase = true) }
            .sortedByDescending { it.lastChangedAt }
            .map { mix ->
                val signature = mix.storedStatsSignature()
                val stats = signature?.let { global.stats(it) }
                if (stats == null && signature != null) {
                    dataGateway.syncStats(createPresenterContext(), mix.storedStatsRequest())
                }
                val summary = stats?.toWageSummary(hourly = false, projectedYears = WM_DEFAULT_PROJECTED_YEARS, currentYear = year)
                WMSavedMixCard(
                    mixId = mix.id,
                    title = mix.title,
                    occupationCount = mix.occupations.count { it.isEnabled },
                    fromJobDescription = mix.fromJobDescription,
                    lastChangedAt = mix.lastChangedAt,
                    areaId = mix.filters.areaId,
                    industryId = mix.filters.industryId,
                    median = summary?.headline?.median,
                    sparkline = summary?.points?.mapNotNull { it.median } ?: emptyList(),
                    topOccupations = mix.topOccupations(names = stats.occupationTitles() + taxonomyNames)
                )
            }
        eventFactory.createListUpdate(cards, hasMixes = mixes.isNotEmpty(), filter = filter, query = query).emit()
    }
}

context(eventFactory: EF, promptFactory: PF, dataGateway: G)
suspend fun <
    P: Prompt,
    E: Event<P>,
    EF: Event.Factory<P, E>,
    PF: Prompt.Factory<P>,
    G: WMSavedMixesDataGateway
> defaultSavedMixesHandlePromptResponse(
    response: FSPrompt.Response<P>,
    /** Attached to every sync a response starts, so the presenter's own sync events reach it. */
    presenterContext: Map<String, Any>,
    emit: suspend E.() -> Unit,
) {
    when (val prompt = response.prompt) {
        is Prompt.RenameMix -> eventFactory.createPromptChange(promptFactory.none()).emit()
        is Prompt.DeleteMix -> {
            val uid = dataGateway.session()?.uid
            if (prompt.selectionOf(response.selected) == Prompt.DeleteMix.Selection.Delete && uid != null) {
                eventFactory.createPromptChange(promptFactory.none(), loading = true).emit()
                dataGateway.deleteMix(uid, prompt.mixId, presenterContext)
            } else {
                eventFactory.createPromptChange(promptFactory.none()).emit()
            }
        }
        else -> defaultHandlePromptResponse(response, emit)
    }
}

typealias WMDefaultSavedMixesPresenter = WMSavedMixesPresenter<Prompt.Base, Event.Base<Prompt.Base>, Action.Base>

fun WMSavedMixesPresenter.Companion.createDefault(
    dataGateway: WMSavedMixesDataGateway,
    instanceId: String = FSPresenter.createInstanceId("WMSavedMixesPresenter"),
    currentYear: () -> Int = { Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).year }
): WMDefaultSavedMixesPresenter = object: WMBaseSavedMixesPresenter<
    Prompt.Base,
    Event.Base<Prompt.Base>,
    Action.Base,
    Event.Factory<Prompt.Base, Event.Base<Prompt.Base>>,
    Action.Factory<Prompt.Base, Action.Base>,
    Prompt.Factory<Prompt.Base>,
    WMSavedMixesDataGateway
>(
    dataGateway = dataGateway,
    eventFactory = Event.baseFactory(),
    actionFactory = Action.baseFactory(),
    promptFactory = Prompt.baseFactory(),
    cancelViewScopeOnUnready = true,
    instanceId = instanceId,
    currentYear = currentYear
) {}
